import { PricingInvoicesResponse } from 'AppGS/api/finance/financeTypes';
import { DiscountType, Nullable, WaitingTimeChargingType } from 'app-types';
import * as financeUtils from 'utils/financeUtils';
import { calculatePercentFee, getBaseValue } from 'utils/financeUtils';
import { camelCase, isNil, isObject } from 'lodash-es';
import {
	HintValues,
	PricingInvoicesResponseExtended,
	ReviewPricingData,
} from './ReviewPricingModalTypes';
import { InvoiceBookingsData } from 'AppProviders/api/invoice';
import { toNegative } from 'utils/converters';

interface AppliedServicePartnerDiscountType {
	appliedServicePartnerDiscountType: DiscountType;
}

export function isDiscountKey(key: Nullable<string>): boolean {
	if (typeof key === 'string') {
		return key.toLowerCase() === 'discount';
	}
	return false;
}

export function isAppliedToWaitingTime<T extends AppliedServicePartnerDiscountType>(
	data: Nullable<T>
): boolean {
	return (
		!isNil(data) && data.appliedServicePartnerDiscountType === DiscountType.baseAndWaitingTime
	);
}

export function isDiscountAppliedToWaitingTime<T extends AppliedServicePartnerDiscountType>(
	key: string,
	data: T
): boolean {
	return isDiscountKey(key) && isAppliedToWaitingTime(data);
}

export function convertInvoiceData(
	invoice: PricingInvoicesResponse
): PricingInvoicesResponseExtended {
	const keysToPercentConvert = ['Discount', 'Gratuity', 'FuelSurcharge', 'Admin'];
	const data = keysToPercentConvert.reduce(
		(prev, key) => {
			let base = getBaseValue(
				invoice.servicePartnerBaseFee,
				invoice.servicePartnerBaseFeeOverwritten
			);
			if (isDiscountAppliedToWaitingTime(key, invoice)) {
				base = base + calculateWaitingTimeFee(invoice);
			}
			const feePercent: Nullable<number> = invoice[`appliedServicePartner${key}Percentages`];
			const fee: Nullable<number> = calculatePercentFee(base, feePercent, null);
			const feeOverwrittenPercent: Nullable<number> =
				invoice[`servicePartner${key}PercentagesOverwritten`];
			const feeOverwritten: Nullable<number> = calculatePercentFee(
				base,
				feeOverwrittenPercent,
				null
			);
			return {
				...prev,
				[camelCase(key) + 'Hints']: {
					fee,
					feePercent,
					feeOverwritten,
				},
			};
		},
		{} as PricingInvoicesResponseExtended
	);
	return {
		...invoice,
		...data,
	};
}

export function calculateWaitingTimeFee(
	data: ReviewPricingData,
	overrideWaitingTime?: number | null
): number {
	const {
		servicePartnerWaitingTimeMinutes,
		servicePartnerWaitingTimeFreeMinutes: freeMinutes,
		servicePartnerWaitingTimeHourlyRate: hourlyRate,
		servicePartnerWaitingTimeChargingType: chargingType,
	} = data;
	const waitingTime = isNil(overrideWaitingTime)
		? servicePartnerWaitingTimeMinutes
		: overrideWaitingTime;
	let waitingTimeCalculation = 0;
	if (waitingTime > freeMinutes) {
		switch (chargingType) {
			case WaitingTimeChargingType.freeMinutes:
				waitingTimeCalculation = (waitingTime - freeMinutes) * (hourlyRate / 60);
				break;
			case WaitingTimeChargingType.gracePeriod:
				waitingTimeCalculation = waitingTime * (hourlyRate / 60);
				break;
			default:
				waitingTimeCalculation = 0;
		}
	} else {
		waitingTimeCalculation = 0;
	}
	return waitingTimeCalculation;
}

export const calculateSpNet = (record: InvoiceBookingsData, waitingTime?: number) => {
	const base = record.servicePartnerBaseFee;
	const keys: Array<keyof InvoiceBookingsData> = [
		'servicePartnerBaseFee',
		'servicePartnerCarParkFee',
		'servicePartnerExtrasFee',
		'servicePartnerBookingFee',
	];
	const servicePartnerWaitingTimeFee = isNil(waitingTime)
		? calculateWaitingTimeFee(record)
		: waitingTime;
	const baseVal = keys.reduce((prev, key) => {
		const value = record[key] || 0;
		return prev + Number(value);
	}, servicePartnerWaitingTimeFee);

	const percentKeys = ['Discount', 'FuelSurcharge', 'Gratuity', 'Admin'];
	const percentValues = percentKeys.reduce((prev, key) => {
		const percent = record[`servicePartner${key}Percentages`] || 0;
		let value = financeUtils.calculatePercentFee(base, percent);
		if (isDiscountKey(key)) {
			if (isAppliedToWaitingTime(record)) {
				value = financeUtils.calculatePercentFee(
					base + servicePartnerWaitingTimeFee,
					percent
				);
			}
			value = toNegative(value);
		}
		return prev + value;
	}, 0);
	return baseVal + percentValues;
};

export const getSummarySp = (record: InvoiceBookingsData, waitingTime?: number) => {
	const net = calculateSpNet(record, waitingTime);
	const vat: Nullable<number> = financeUtils.calculatePercentFee(
		net,
		record.internalServicePartnerCarTypeVatRatePercentage,
		null
	);
	return {
		net,
		vat,
		total: net + (vat || 0),
	};
};

export function isHintData(data: unknown): data is HintValues {
	return (
		isObject(data) &&
		Object.keys(data).some(k => {
			return ['fee', 'feePercent', 'feeOverwritten'].includes(k);
		})
	);
}
